// Added by Sejune Oh on 2019-03-22
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC } from 'react'
import styled from 'styled-components'
import { withTheme, WithTheme } from '@material-ui/core'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Prime Elements
import { FormControl, FormHelperText, TextField, FormLabel } from '../../_primes'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import { FormControlProps } from '@material-ui/core/FormControl'
import { BaseTextFieldProps } from '@material-ui/core/TextField'
import { FormLabelProps } from '@material-ui/core/FormLabel'
import { InputProps as StandardInputProps } from '@material-ui/core/Input'
import { OutlinedInputProps } from '@material-ui/core/OutlinedInput'
import { FilledInputProps } from '@material-ui/core/FilledInput'
import { ReactKeyDownEvent } from '@/helper/define'
import { RED_COLOR } from '@/styles/Theme'

export interface InputBaseFCProps extends BaseTextFieldProps, InputCommonProps {
    inputComponent?: JSX.Element
    errMessage?: string
    isErrorOccured?: boolean
    isFullWidth?: boolean
    inline?: string
    hidden?: boolean
    condition?: string
    variant?: 'standard' | 'outlined' | 'filled'
    InputProps?: Partial<StandardInputProps | OutlinedInputProps | FilledInputProps>
    inputProps?: StandardInputProps['inputProps'] | OutlinedInputProps['inputProps'] | FilledInputProps['inputProps']
}

export interface InputCommonProps {
    isErrorOccured?: boolean
    onInputKeyDown?: ReactKeyDownEvent
    min?: number
    max?: number
    step?: string
    readOnly?: boolean
}

interface InputFCProps extends InputBaseFCProps, WithTheme {
    description?: string
}

interface ExtendedFormControlProps extends FormControlProps {
    inline?: string
    description?: string
} 

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const Input: FC<InputFCProps> = ({ id, ...props }) => {
    const subTextStyle = props.isErrorOccured ? { color: RED_COLOR } : undefined
    const subText = props.isErrorOccured ? props.errMessage : props.description

    return (
        <StyledFormContorl
            className={props.className}
            inline={props.inline}
            fullWidth={props.isFullWidth}
            error={props.isErrorOccured}
            margin={props.margin}
            variant={props.variant}
            hidden={props.hidden}
            theme={props.theme}
            description={props.description}
        >
            {props.condition && (
                <StyledFormLabel theme={props.theme} data-cy-input-condition={`${id}_condition`}>{props.condition}</StyledFormLabel>
            )}
            {props.inputComponent}
            <FormHelperText id="input-helper-text" hidden={props.hidden} data-text={subText} />
            {props.isFullWidth ? (
                <StyledDescription theme={props.theme} style={subTextStyle} data-cy-input-desc={`${id}_desc`} data-hastext={!!subText}>
                    {subText}
                </StyledDescription>
            ) : (
                    <StyledShortDescription theme={props.theme} style={subTextStyle} data-cy-input-desc={`${id}_desc`} data-hastext={!!subText}>
                        {subText}
                    </StyledShortDescription>
                )}
        </StyledFormContorl>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Style
const StyledFormContorl = styled(FormControl as FC<ExtendedFormControlProps>)`
    && {
        padding: ${(props: InputFCProps) => props.theme.spacing()}px;
        padding-top: ${(props: InputFCProps) => props.theme.spacing() * 2}px;
        padding-bottom: ${(props: InputFCProps) => props.theme.spacing() / 2}px;
        margin: 0;
        width: ${(props: InputFCProps) => (props.fullWidth ? '100%' : '50%')};
        flex-grow: ${(props: InputFCProps) => (props.fullWidth ? 1 : 0.5)};
        display: ${(props: InputFCProps) => (props.inline === 'true' ? 'inline-flex' : 'flex')};
        position: relative;

        & label {
            font-size: 1.3rem;
            font-family: inherit;
            font-weight: normal;
        }

        & label.Mui-focused {
            font-weight: bold;
        }

        @media (max-width: 990px) {
            width: 100%;
            flex-grow: 1;
        }
    }
`

const StyledFormLabel = styled(FormLabel as FC<FormLabelProps>)`
    && {
        position: absolute;
        top: 47px;
        right: 16px;
        color: rgba(255, 255, 255, 0.4);
        font-size: 1rem !important;
    }
`

export const StyledTextField = styled(TextField as FC<InputFCProps>)`
    &[data-error='true'] {
        & > label {
            color: ${(props: InputFCProps) => props.theme.palette.error.main};
        }
        & > div {
            &::before,
            &::after {
                border-color: ${(props: InputFCProps) => props.theme.palette.error.main};
            }
        }
    }

    && {
        input[type='number']::-webkit-outer-spin-button,
        input[type='number']::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
`

const StyledDescription = styled.span`
    left: 13px;
    color: rgba(255, 255, 255, 0.7);
    right: 0;
    bottom: 6px;
    width: max-content;
    font-size: 1rem;
    min-height: 1rem;
    line-height: 1rem;

    &[data-hastext='false'] {
        display: none;
    }
`
const StyledShortDescription = styled.span`
    left: 13px;
    color: rgba(255, 255, 255, 0.7);
    right: -100%;
    bottom: 6px;
    width: max-content;
    font-size: 1rem;
    min-height: 1rem;
    line-height: 1rem;

    &[data-hastext='false'] {
        display: none;
    }

    @media (max-width: 990px) {
        right: 0;
    }
`

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default withTheme(Input)
